import React, { useEffect, useState, useCallback } from 'react'
import { Card, Button, Spinner } from 'react-bootstrap'
import { getNetworkStatus, scanWifiNetworks, connectWifi, disconnectWifi } from '../generic/crud'
import { showToast, TOAST_SUCCESS, TOAST_FAILURE } from '../../utils/toast/Toast'
import { NetworkRow } from './NetworkRow'
import { NetworkStatusCard } from './NetworkStatusCard'
import { WifiConnectModal, WifiDisconnectModal } from './WifiConnectModal'

const WifiSettingsContent = () => {
    const [networkStatus, setNetworkStatus] = useState(null)
    const [networks, setNetworks] = useState([])
    const [scanning, setScanning] = useState(false)
    const [connecting, setConnecting] = useState(false)
    const [disconnecting, setDisconnecting] = useState(false)
    const [statusLoading, setStatusLoading] = useState(true)

    const [showConnectModal, setShowConnectModal] = useState(false)
    const [selectedSsid, setSelectedSsid] = useState('')
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)

    const [showDisconnectModal, setShowDisconnectModal] = useState(false)
    const [disconnectSsid, setDisconnectSsid] = useState('')

    const fetchNetworkStatus = useCallback(() => {
        getNetworkStatus()
            .then(({ data }) => {
                setNetworkStatus(data)
                setStatusLoading(false)
            })
            .catch(() => setStatusLoading(false))
    }, [])

    useEffect(() => {
        fetchNetworkStatus()
        const interval = setInterval(fetchNetworkStatus, 5000)
        return () => clearInterval(interval)
    }, [fetchNetworkStatus])

    const handleScan = () => {
        setScanning(true)
        scanWifiNetworks()
            .then(({ data }) => {
                if (data.success) setNetworks(data.networks || [])
                else showToast(TOAST_FAILURE, data.error || 'Scan failed')
            })
            .catch(() => showToast(TOAST_FAILURE, 'Failed to scan networks'))
            .finally(() => setScanning(false))
    }

    const handleConnect = () => {
        setConnecting(true)
        connectWifi(selectedSsid, password)
            .then(({ data }) => {
                if (data.success) {
                    showToast(TOAST_SUCCESS, data.message)
                    setShowConnectModal(false)
                    setPassword('')
                    fetchNetworkStatus()
                    handleScan()
                } else {
                    showToast(TOAST_FAILURE, data.error || 'Connection failed')
                }
            })
            .catch(() => showToast(TOAST_FAILURE, 'Connection failed'))
            .finally(() => setConnecting(false))
    }

    const handleDisconnect = () => {
        setDisconnecting(true)
        disconnectWifi(disconnectSsid)
            .then(({ data }) => {
                if (data.success) {
                    showToast(TOAST_SUCCESS, data.message)
                    setShowDisconnectModal(false)
                    fetchNetworkStatus()
                    handleScan()
                } else {
                    showToast(TOAST_FAILURE, data.error || 'Disconnect failed')
                }
            })
            .catch(() => showToast(TOAST_FAILURE, 'Disconnect failed'))
            .finally(() => setDisconnecting(false))
    }

    const openConnectModal = (ssid) => {
        setSelectedSsid(ssid)
        setPassword('')
        setShowPassword(false)
        setShowConnectModal(true)
    }

    const openDisconnectModal = (ssid) => {
        setDisconnectSsid(ssid)
        setShowDisconnectModal(true)
    }

    return (
        <>
            <NetworkStatusCard statusLoading={statusLoading} networkStatus={networkStatus} />

            <Card>
                <Card.Header className='border-0 pt-5'>
                    <Card.Title className='align-items-start flex-column'>
                        <span className='fw-bold fs-3'>WiFi Configuration</span>
                        <span className='text-muted mt-1 fw-semibold fs-7'>Scan and connect to available networks</span>
                    </Card.Title>
                    <div className='card-toolbar'>
                        <Button variant='primary' size='sm' onClick={handleScan} disabled={scanning}>
                            {scanning ? (<><Spinner animation='border' size='sm' className='me-2' />Scanning...</>)
                                      : (<><i className='bi bi-arrow-clockwise me-2' />Scan Networks</>)}
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body className='pt-0'>
                    {networks.length === 0 ? (
                        <div className='text-center py-10 text-muted'>
                            <i className='bi bi-wifi-off fs-1 d-block mb-3' />
                            Click "Scan Networks" to discover available WiFi networks
                        </div>
                    ) : (
                        <div className='table-responsive'>
                            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                                <thead>
                                    <tr className='fw-bold text-muted'>
                                        <th>Network</th>
                                        <th className='text-center'>Signal</th>
                                        <th className='text-center'>Security</th>
                                        <th className='text-center'>Status</th>
                                        <th className='text-end'>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {networks.map((net) => (
                                        <NetworkRow
                                            key={net.ssid}
                                            network={net}
                                            onConnect={openConnectModal}
                                            onDisconnect={openDisconnectModal}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <WifiConnectModal
                show={showConnectModal}
                ssid={selectedSsid}
                password={password}
                showPassword={showPassword}
                connecting={connecting}
                onPasswordChange={setPassword}
                onToggleShowPassword={() => setShowPassword(!showPassword)}
                onHide={() => setShowConnectModal(false)}
                onConnect={handleConnect}
            />

            <WifiDisconnectModal
                show={showDisconnectModal}
                ssid={disconnectSsid}
                disconnecting={disconnecting}
                onHide={() => setShowDisconnectModal(false)}
                onDisconnect={handleDisconnect}
            />
        </>
    )
}

export { WifiSettingsContent }
